import React, { useEffect, useRef, useState } from 'react';
import { ParentPlanningControls } from './ParentPlanningControls';
import { GroupOptimizerPlanPanel } from './GroupOptimizerPlanPanel';
import { useApiClient } from '../api/client';
import { useI18n } from '../i18n/I18nProvider';

type Preview = { counts: Record<string, number>; directionsCalls: number; problems: string[]; warnings: string[] };
function currentMonday() {
  const date = new Date(); date.setDate(date.getDate() - (date.getDay() + 6) % 7);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function GroupOptimizerExportDialog({ communityId, groupId, groupName, onClose }: {
  communityId: string; groupId: string; groupName: string; onClose: () => void;
}) {
  const { request } = useApiClient(); const { t } = useI18n();
  const dialog = useRef<HTMLDialogElement>(null);
  const [start, setStart] = useState(currentMonday);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(true);
  const [planning, setPlanning] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [downloaded, setDownloaded] = useState(false);
  const [reload, setReload] = useState(0);
  const base = `/admin/communities/${encodeURIComponent(communityId)}/groups/${encodeURIComponent(groupId)}/optimizer-export`;
  const monday = /^\d{4}-\d{2}-\d{2}$/.test(start) && new Date(`${start}T12:00:00`).getDay() === 1;
  useEffect(() => {
    const element = dialog.current!; element.showModal();
    return () => element.close();
  }, []);
  useEffect(() => {
    let active = true; setPreview(null); setError(''); setDownloaded(false); setLoading(monday);
    if (monday) request<Preview>(`${base}/preview?start=${start}`, { cache: 'no-store' })
      .then(value => { if (active) setPreview(value); })
      .catch(err => { if (active) setError(err.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [base, start, monday, request, reload]);

  async function download() {
    if (!preview || preview.problems.length || exporting || loading) return;
    setExporting(true); setError(''); setDownloaded(false);
    try {
      const scenario = await request<unknown>(base, { method: 'POST', body: JSON.stringify({ start }), cache: 'no-store' });
      const url = URL.createObjectURL(new Blob([JSON.stringify(scenario, null, 2)], { type: 'application/json' }));
      const link = document.createElement('a'); link.href = url; link.download = `fahrgruppe-${groupId}-${start}.json`;
      document.body.appendChild(link); link.click(); link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000); setDownloaded(true);
    } catch (err) { setError(err instanceof Error ? err.message : t('optimizer.failed')); }
    finally { setExporting(false); }
  }
  return <dialog ref={dialog} className="admin-optimizer-dialog" aria-labelledby="optimizer-title"
    onCancel={event => { event.preventDefault(); if (!exporting) onClose(); }}>
    <div className="p-6 border-b border-slate-200">
      <p className="text-sm text-slate-500 mb-2">{groupName}</p>
      <h2 id="optimizer-title" className="text-xl font-semibold">{t('optimizer.title')}</h2>
      <p className="mt-2 text-sm text-slate-600">{t('optimizer.description')}</p>
    </div>
    <div className="p-6 space-y-5" aria-busy={loading || exporting}>
      <div><label className="admin-form-label" htmlFor="optimizer-week">{t('optimizer.week')}</label>
        <input autoFocus id="optimizer-week" type="date" className="form-control mt-2 w-full" value={start} disabled={exporting || planning}
          onChange={event => { setPreview(null); setStart(event.target.value); }} />
      </div>
      {!monday && <p role="alert" className="text-sm text-red-700">{t('optimizer.monday')}</p>}
      {loading && <p role="status" className="text-sm text-slate-500">{t('optimizer.loading')}</p>}
      {preview && <>
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
          {['families', 'children', 'requests', 'drivers', 'vehicles', 'locations'].map(key => <div key={key}>
            <dt className="text-xs text-slate-500">{t(`optimizer.${key}`)}</dt><dd className="mt-1 text-xl font-semibold tabular-nums">{preview.counts[key]}</dd>
          </div>)}
        </dl>
        {preview.problems.length > 0 && <div role="alert" className="rounded-lg bg-red-50 p-4 text-sm text-red-800">
          <p className="font-semibold mb-2">{t('optimizer.fix')}</p><ul className="list-disc pl-4 space-y-1">{preview.problems.map(p => <li key={p}>{p}</li>)}</ul>
        </div>}
        {preview.warnings.length > 0 && <ul className="rounded-lg bg-amber-50 p-4 text-sm text-amber-900 space-y-2">{preview.warnings.map(w => <li key={w}>{w}</li>)}</ul>}
        <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-600">
          <p className="font-medium text-slate-800">{t('optimizer.calls', { count: preview.directionsCalls })}</p>
          <p className="mt-1">{t('optimizer.cost')}</p>
        </div>
      </>}
      <p className="text-xs leading-relaxed text-slate-500">{t('optimizer.scope')}</p>
      {error && <p role="alert" className="whitespace-pre-line text-sm text-red-700">{error}</p>}
      {monday && <GroupOptimizerPlanPanel base={base.replace(/optimizer-export$/, 'optimizer-plans')} start={start}
        canCalculate={!loading && !exporting && !!preview && !preview.problems.length} onBusyChange={setPlanning} />}
      {monday && <ParentPlanningControls key={`${groupId}:${start}`} groupId={groupId} start={start} />}
      {downloaded && <p role="status" className="text-sm text-green-800">{t('optimizer.downloaded')}</p>}
      {exporting && <p role="status" className="text-sm text-slate-600">{t('optimizer.running')}</p>}
      {!loading && !exporting && (error || !!preview?.problems.length) && <button className="btn btn-neutral" onClick={() => setReload(n => n + 1)}>{t('optimizer.refresh')}</button>}
    </div>
    <div className="p-5 border-t border-slate-200 flex flex-wrap justify-end gap-2">
      <button className="btn btn-neutral" disabled={exporting} onClick={onClose}>{t('optimizer.close')}</button>
      <button className="btn btn-neutral" disabled={!monday || !preview || !!preview.problems.length || loading || exporting || planning} onClick={download}>{t('optimizer.download')}</button>
    </div>
  </dialog>;
}
